import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow, TableFooter
} from "@/components/ui/table";
import { Receipt } from "@phosphor-icons/react";

const SERVICE_LABELS = {
  accommodation: "Accommodation",
  icu: "ICU",
  nursing: "Nursing",
  medicine: "Medicine",
  consumable: "Consumable",
  investigation: "Investigation",
  procedure: "Procedure",
  consultation: "Consultation",
  other: "Other"
};

const SOURCE_LABELS = {
  hospital_bill: "Hospital Bill",
  hospital_bill_summary: "Summary Bill",
  hospital_bill_detailed: "Detailed Bill",
  pharmacy_bill: "Pharmacy Bill",
  pre_hospitalization_bill: "Pre-Hosp",
  post_hospitalization_bill: "Post-Hosp"
};

export default function BillsTab({ claim }) {
  const lineItems = claim.line_items || [];
  const isProcessed = claim.status === "processed";

  if (!isProcessed || lineItems.length === 0) {
    return (
      <div className="p-6 text-center text-gray-400" data-testid="bills-empty">
        <Receipt size={48} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">
          {isProcessed ? "No line items extracted from the documents." : "Process the claim to view bill details."}
        </p>
      </div>
    );
  }

  const totalClaimed = lineItems.reduce((sum, li) => sum + (Number(li.amount) || 0), 0);

  // Group by source type
  const grouped = {};
  lineItems.forEach(li => {
    const src = li.source_type || "other";
    if (!grouped[src]) grouped[src] = [];
    grouped[src].push(li);
  });

  const fmt = (n) => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  return (
    <div className="p-6 space-y-6" data-testid="bills-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="border border-gray-200 rounded-md p-3">
          <p className="label-xs mb-1">Total Line Items</p>
          <p className="text-xl font-bold text-gray-900" data-testid="line-items-count">{lineItems.length}</p>
        </div>
        <div className="border border-gray-200 rounded-md p-3">
          <p className="label-xs mb-1">Total Claimed</p>
          <p className="text-xl font-bold text-gray-900" data-testid="total-claimed">INR {fmt(totalClaimed)}</p>
        </div>
        <div className="border border-gray-200 rounded-md p-3">
          <p className="label-xs mb-1">Bill Sources</p>
          <p className="text-xl font-bold text-gray-900">{Object.keys(grouped).length}</p>
        </div>
      </div>

      {/* Grouped tables */}
      {Object.entries(grouped).map(([source, items]) => {
        const groupTotal = items.reduce((s, li) => s + (Number(li.amount) || 0), 0);
        return (
          <div key={source} className="border border-gray-200 rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  {SOURCE_LABELS[source] || source.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </h4>
                <Badge variant="outline" className="text-xs">{items.length} items</Badge>
              </div>
              <span className="text-sm font-semibold text-gray-700">INR {fmt(groupTotal)}</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 w-8">#</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Qty</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Rate</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Page</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((li, idx) => (
                  <TableRow key={idx} className="hover:bg-gray-50">
                    <TableCell className="text-xs text-gray-400">{idx + 1}</TableCell>
                    <TableCell className="text-sm text-gray-900 font-medium max-w-[200px] truncate">
                      {li.description}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {SERVICE_LABELS[li.service_type] || li.service_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 text-right">{li.quantity}</TableCell>
                    <TableCell className="text-sm text-gray-700 text-right">{fmt(li.rate)}</TableCell>
                    <TableCell className="text-sm font-medium text-gray-900 text-right">{fmt(li.amount)}</TableCell>
                    <TableCell className="text-xs text-gray-400 text-right">P{li.source_page}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5} className="text-xs font-semibold text-gray-700 text-right">Subtotal</TableCell>
                  <TableCell className="text-sm font-bold text-gray-900 text-right">{fmt(groupTotal)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        );
      })}
    </div>
  );
}
